/**
 * SidebarWorkspaceSwitcher
 *
 * The tenant picker. Workspace is the top-level tenant, so this is the only
 * dimension there is — one control, no hierarchy to express.
 *
 * Reads WorkspaceContext directly rather than taking the session as a prop. The
 * sidebar used to render `session.user.memberships`, which the auth layer built
 * by mapping each workspace_members row 1:1 onto an "Organization" — so the same
 * rows were fetched twice, by two different queries that could disagree on
 * ordering, and the control called itself an organization switcher while
 * switching workspaces.
 *
 * Chrome appears only where it does something: with a single workspace this is a
 * plain label — no chevron, no hover. That is the common case, and a dropdown
 * that cannot go anywhere is a lie about what the control does.
 *
 * @module components/workspace/SidebarWorkspaceSwitcher
 */

import { useState, useRef, useCallback } from "react";
import { ChevronDown, Layers } from "lucide-react";
import { clsx } from "clsx";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useDismissable } from "@/hooks/useDismissable";
import { useTranslation } from "react-i18next";

export const SidebarWorkspaceSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const { workspace, workspaces, switchWorkspace } = useWorkspace();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setIsOpen(false), []);

  useDismissable(containerRef, isOpen, close);

  if (!workspace) return null;

  if (workspaces.length < 2) {
    return (
      /* `title` matters MORE here than on the button above, not less: with one
         workspace there is no dropdown to fall back on, so a truncated name has
         nowhere else to be read in full. */
      <div className="flex items-center gap-2 px-3 py-2 text-sm" title={workspace.name}>
        <Layers size={16} className="text-primary-500 shrink-0" aria-hidden="true" />
        <span className="font-medium text-ink-100 truncate">{workspace.name}</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm bg-ink-900 text-ink-100 rounded-lg hover:bg-ink-800 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary-500"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={t("common.currentWorkspace", { name: workspace.name })}
        /* The trigger DOES truncate — it has to fit a fixed sidebar — so the
           full name is offered here for a pointer. Not a substitute for the
           list below being readable: `title` never appears on touch, and a
           screen reader already has the name from aria-label. */
        title={workspace.name}
      >
        <span className="flex items-center gap-2 min-w-0">
          <Layers size={16} className="text-primary-500 shrink-0" aria-hidden="true" />
          <span className="font-medium truncate">{workspace.name}</span>
        </span>
        <ChevronDown
          size={16}
          className={clsx("text-ink-400 shrink-0 transition-transform", isOpen && "rotate-180")}
          aria-hidden="true"
        />
      </button>

      {/* Plain buttons, deliberately NOT role="listbox"/"option". Those roles
          promise a keyboard contract — arrow-key navigation, managed focus,
          aria-activedescendant — that this control does not implement, and a
          screen-reader user told to press Down would find nothing happens. As
          real buttons in DOM order they are Tab-reachable and announced
          correctly, which is the honest description of what this is. */}
      {/* max-h + overflow-y-auto, NOT the overflow-hidden that used to be the
          only thing here. That one clips the rounded corners and nothing else,
          so the panel grew one row per workspace with no height limit and no
          scrollbar: past the bottom of the viewport the remaining workspaces
          were simply unreachable — the panel is absolutely positioned, so the
          page does not grow a scrollbar to reveal them either, and the sidebar
          wrapper (Layout.tsx) sets no overflow of its own to catch it.

          60vh rather than a row count: the limit that matters is the screen's,
          and a laptop in a browser with devtools open has far less room than
          the number of workspaces suggests. `overscroll-contain` keeps a scroll
          that reaches the end of this list from carrying on into the page
          behind it.

          AND IT IS AS WIDE AS ITS CONTENT, not as wide as the sidebar. It used
          to be `left-0 right-0` — pinned to the trigger, so every name longer
          than a narrow column was cut with an ellipsis and could not be read at
          all. The trigger has to fit the sidebar; this list does not, and
          spilling over the page beside it costs nothing because it is a
          transient overlay that closes on the next click.

          `min-w-full` keeps it from ever being NARROWER than the trigger, and
          the cap is `min(24rem, 100vw - 2rem)`: 24rem is about 45 characters,
          past which a name stops being scannable anyway, and the viewport term
          is what keeps it on screen inside the mobile drawer. A name longer
          than the cap wraps rather than truncating, so the full text is always
          reachable without hovering — which matters because hover does not
          exist on touch. */}
      {isOpen && (
        <div className="absolute left-0 mt-1 min-w-full w-max max-w-[min(24rem,calc(100vw-2rem))] max-h-[60vh] overflow-y-auto overscroll-contain scrollbar-thin bg-ink-900 border border-ink-700 rounded-lg shadow-lg z-10">
          {workspaces.map((ws) => {
            const isCurrent = ws.id === workspace.id;
            return (
              <button
                key={ws.id}
                type="button"
                onClick={() => {
                  void switchWorkspace(ws.id);
                  close();
                }}
                className={clsx(
                  "w-full px-3 py-2 text-sm text-left transition-colors",
                  isCurrent
                    ? "bg-primary-500 text-ink-950 hover:bg-primary-400 font-semibold"
                    : "text-ink-100 hover:bg-ink-800"
                )}
                aria-current={isCurrent ? "true" : undefined}
              >
                <span className="flex items-start justify-between gap-2">
                  {/* No `truncate` here, unlike the trigger above. This list is
                      sized to its content, so a name that still does not fit
                      wraps and stays readable — where an ellipsis leaves a
                      reader with two workspaces they cannot tell apart.
                      `items-start` so the role badge stays on the first line of
                      a wrapped name rather than floating to its middle. */}
                  <span className="break-words">{ws.name}</span>
                  {/* ink-400 (#888) on ink-900 (#0a0a0a) ≈ 5.6:1 — clears WCAG AA
                      for small text. ink-500 (#555) measures 2.66:1 and fails. */}
                  <span
                    className={clsx(
                      "text-xs shrink-0",
                      isCurrent ? "text-ink-800" : "text-ink-400"
                    )}
                  >
                    {ws.userRole}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SidebarWorkspaceSwitcher;
